import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Subscription, combineLatest, interval } from 'rxjs';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-navigation-bar',
  imports: [CommonModule],
  templateUrl: './navigation-bar.component.html',
  styleUrl: './navigation-bar.component.scss'
})
export class NavigationBarComponent implements OnInit, OnDestroy {
  isAuthenticated = false;
  isTokenExpired = false;
  username = '';
  tokenExpiryTime: Date | null = null;

  private authSubscription?: Subscription;
  private timerSubscription?: Subscription;

  constructor(
    private oidcSecurityService: OidcSecurityService,
    private router: Router
  ) {}

  ngOnInit() {
    // Check authentication status and token validity every few seconds
    this.authSubscription = combineLatest([
      this.oidcSecurityService.isAuthenticated(),
      this.oidcSecurityService.getAccessToken(),
      interval(3000) // Check every 3 seconds
    ]).subscribe(async ([isAuthenticated, accessToken]) => {
      this.isAuthenticated = isAuthenticated && !!accessToken && accessToken.trim() !== '';
      
      if (accessToken) {
        this.checkTokenExpiry(accessToken);
        await this.loadUsername();
      } else {
        this.isTokenExpired = true;
        this.username = '';
        this.tokenExpiryTime = null;
      }
    });

    // Update token expiry status every second
    this.timerSubscription = interval(1000).subscribe(() => {
      this.updateTokenExpiryStatus();
    });
  }

  ngOnDestroy() {
    this.authSubscription?.unsubscribe();
    this.timerSubscription?.unsubscribe();
  }

  private checkTokenExpiry(token: string) {
    try {
      const decoded = this.decodeJWT(token);
      if (decoded && decoded.exp) {
        const currentTime = Math.floor(Date.now() / 1000);
        this.isTokenExpired = decoded.exp < currentTime;
        this.tokenExpiryTime = new Date(decoded.exp * 1000);
      } else {
        this.isTokenExpired = true;
        this.tokenExpiryTime = null;
      }
    } catch (error) {
      console.error('Error checking token expiry:', error);
      this.isTokenExpired = true;
      this.tokenExpiryTime = null;
    }
  }

  private async loadUsername() {
    try {
      const userData = await firstValueFrom(this.oidcSecurityService.getUserData());
      this.username = userData?.['cognito:username'] || userData?.username || userData?.preferred_username || '';
    } catch (error) {
      console.error('Error loading username:', error);
      this.username = '';
    }
  }

  private updateTokenExpiryStatus() {
    if (!this.tokenExpiryTime) {
      return;
    }

    const now = new Date();
    const timeDiff = this.tokenExpiryTime.getTime() - now.getTime();

    if (timeDiff <= 0) {
      this.isTokenExpired = true;
    }
  }

  private decodeJWT(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding JWT:', error);
      return null;
    }
  }

  login() {
    if (this.isAuthenticated && !this.isTokenExpired) {
      // If user is authenticated and token is not expired, log out first then authorize
      // This allows switching to a different AWS Cognito user
      this.oidcSecurityService.logoff().subscribe(() => {
        // After logout, redirect to authorization to allow login with different credentials
        this.oidcSecurityService.authorize();
      });
    } else {
      // If not authenticated or token is expired, go through authorization flow
      this.oidcSecurityService.authorize();
    }
  }

  getAuthStatus(): string {
    if (!this.isAuthenticated) {
      return '未登入';
    } else if (this.isTokenExpired) {
      return '登入已過期';
    } else {
      return '已登入';
    }
  }

  getAuthStatusClass(): string {
    if (!this.isAuthenticated || this.isTokenExpired) {
      return 'status-error';
    } else {
      return 'status-success';
    }
  }

  getLoginButtonText(): string {
    if (!this.isAuthenticated || this.isTokenExpired) {
      return '登入';
    } else {
      return '切換管理員';
    }
  }

  getSessionExpiredMessage(): string {
    return '你的登入已過期，請重新登入。'
  }
} 